import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import Lenis from 'lenis';
import { Phone } from 'lucide-react';
import { Toaster } from 'sonner';
import '@/App.css';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ChatWidget from '@/components/ChatWidget';
import { SITE } from '@/data/site';
import { initTracking, trackPageView, setupClickTracking } from '@/lib/track';
import AdminApp from '@/admin/AdminApp';
import LoginPage from '@/admin/LoginPage';
import HomePage from '@/pages/HomePage';
import ServicesPage from '@/pages/ServicesPage';
import ServiceDetailPage from '@/pages/ServiceDetailPage';
import ServiceCityPage from '@/pages/ServiceCityPage';
import LocationsPage from '@/pages/LocationsPage';
import LocationPage from '@/pages/LocationPage';
import ProjectsPage from '@/pages/ProjectsPage';
import ProjectPage from '@/pages/ProjectPage';
import AboutPage from '@/pages/AboutPage';
import ContactPage from '@/pages/ContactPage';
import QuotePage from '@/pages/QuotePage';
import ThankYouPage from '@/pages/ThankYouPage';
import BlogPage from '@/pages/BlogPage';
import BlogPostPage from '@/pages/BlogPostPage';
import LegalPage from '@/pages/LegalPage';
import NotFoundPage from '@/pages/NotFoundPage';
import { Navigate } from 'react-router-dom';

const ScrollManager = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || isAdmin) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, [isAdmin]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Tracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith('/admin')) return;
    initTracking(pathname);
    setupClickTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (pathname.startsWith('/admin')) return;
    trackPageView(pathname);
  }, [pathname]);
  return null;
};

const MobileStickyCTA = () => {
  const { pathname } = useLocation();
  if (pathname === '/get-a-quote/' || pathname === '/thank-you/' || pathname.startsWith('/admin')) return null;
  return (
    <div data-testid="mobile-sticky-cta" className="fixed inset-x-0 bottom-0 z-40 flex border-t border-line bg-paper/95 backdrop-blur-lg lg:hidden">
      <a href={SITE.phoneHref} data-testid="sticky-call-btn" className="flex flex-1 items-center justify-center gap-2 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink">
        <Phone className="h-4 w-4" /> Call
      </a>
      <Link to="/get-a-quote/" data-testid="sticky-quote-btn" className="flex flex-[1.4] items-center justify-center bg-bronze py-4 text-sm font-semibold uppercase tracking-[0.12em] text-paper">
        Get a Free Quote
      </Link>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollManager />
        <Tracker />
        <Routes>
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin/*" element={<AdminApp />} />
          <Route
            path="*"
            element={
              <>
                <PublicSite />
                <MobileStickyCTA />
                <ChatWidget />
              </>
            }
          />
        </Routes>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

const PublicSite = () => (
  <>
    <Header />
    <main className="pb-[57px] lg:pb-0">
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services/" element={<ServicesPage />} />
        <Route path="/services/:slug/:city/" element={<ServiceCityPage />} />
        <Route path="/services/:slug/" element={<ServiceDetailPage />} />
        <Route path="/patios" element={<Navigate to="/services/patios/" replace />} />
        <Route path="/service-areas/" element={<LocationsPage />} />
        <Route path="/service-areas/:slug/" element={<LocationPage />} />
        <Route path="/projects/" element={<ProjectsPage />} />
        <Route path="/projects/:slug/" element={<ProjectPage />} />
        <Route path="/about/" element={<AboutPage />} />
        <Route path="/contact/" element={<ContactPage />} />
        <Route path="/get-a-quote/" element={<QuotePage />} />
        <Route path="/thank-you/" element={<ThankYouPage />} />
        <Route path="/blog/" element={<BlogPage />} />
        <Route path="/blog/:slug/" element={<BlogPostPage />} />
        <Route path="/privacy-policy/" element={<LegalPage type="privacy" />} />
        <Route path="/terms/" element={<LegalPage type="terms" />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </main>
    <Footer />
  </>
);

export default App;
